import React from 'react';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useLocation } from 'react-router-dom';
import useScenario from 'modules/project/providers/scenario/use';
import { ENVIRONMENT_BANNER_HEIGHT, getEnvironment } from 'components/EnvironmentBanner/EnvironmentBanner';

export const SCENARIO_BANNER_HEIGHT = 28;

// The scenario banner is hidden on the reports (/reports) page.
export const isScenarioBannerHiddenForPath = (pathname: string): boolean => /\/reports(\/|$)/.test(pathname);

const ScenarioBanner = () => {
  const { activeScenarioId, activeScenarioName, isSwitching, switchTarget } = useScenario();
  const { pathname } = useLocation();

  const showBanner = !!activeScenarioId && !isScenarioBannerHiddenForPath(pathname);
  const targetLabel = switchTarget ?? (activeScenarioName ?? 'Master');
  const bottomOffset = getEnvironment() ? ENVIRONMENT_BANNER_HEIGHT : 0;

  return (
    <>
      {showBanner && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: bottomOffset,
            zIndex: 10000,
            backgroundColor: '#ed6c02',
            color: 'common.white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 0.25,
            height: SCENARIO_BANNER_HEIGHT,
            fontFamily: 'sans-serif',
            boxShadow: '0 -2px 6px rgba(0,0,0,0.2)',
          }}
        >
          <AccountTreeIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Scenario:
            {' '}
            {activeScenarioName ?? activeScenarioId}
          </Typography>
        </Box>
      )}

      <Fade in={isSwitching} timeout={{ enter: 180, exit: 320 }} unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 13000,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            pointerEvents: 'all',
            color: 'common.white',
          }}
        >
          <CircularProgress color="inherit" size={40} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Switching to
            {' '}
            {targetLabel}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Loading model, loops, pipes and stopes…
          </Typography>
        </Box>
      </Fade>
    </>
  );
};

export default ScenarioBanner;
